import { useState, useCallback } from 'react';
import { updateInsuranceCompany } from '../api/insurance';
import type { UpdateInsuranceCompanyRequest, InsuranceCompanyDto } from '../types/insurance';

interface UseUpdateInsuranceCompanyResult {
  update: (id: number, data: UpdateInsuranceCompanyRequest) => Promise<InsuranceCompanyDto>;
  isLoading: boolean;
  error: string | null;
  reset: () => void;
}

export function useUpdateInsuranceCompany(
  onSuccess?: (company: InsuranceCompanyDto) => void
): UseUpdateInsuranceCompanyResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = useCallback(
    async (id: number, data: UpdateInsuranceCompanyRequest): Promise<InsuranceCompanyDto> => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await updateInsuranceCompany(id, data);
        onSuccess?.(result);
        return result;
      } catch (err) {
        const apiError = err as { response?: { data?: { message?: string } } };
        const message = apiError.response?.data?.message || 'Failed to update insurance company';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [onSuccess]
  );

  const reset = useCallback(() => {
    setError(null);
  }, []);

  return { update, isLoading, error, reset };
}
